import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, apiError, tl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Loader2, Wallet as WalletIcon, Gift, Sparkles, ArrowDownCircle, ArrowUpCircle, Check, ShieldCheck, Lock, Wrench as WrenchIcon } from "lucide-react";
import { toast } from "sonner";

const TX_LABELS = {
  deposit: { label: "Bakiye Yükleme", icon: ArrowDownCircle, color: "text-emerald-600" },
  promotional_bonus: { label: "Bonus", icon: Gift, color: "text-violet-600" },
  contact_unlock: { label: "İletişim Açma", icon: ArrowUpCircle, color: "text-slate-600" },
  refund: { label: "İade", icon: ArrowDownCircle, color: "text-emerald-600" },
  adjustment: { label: "Düzenleme", icon: Sparkles, color: "text-amber-600" },
  boost: { label: "Öne Çıkarma", icon: Sparkles, color: "text-amber-600" },
};

export default function Wallet() {
  const { config } = useAuth();
  const [wallet, setWallet] = useState(null);
  const [txs, setTxs] = useState([]);
  const [loading, setLoading] = useState(null);
  const [agreed, setAgreed] = useState(false);

  const load = async () => {
    const [{ data: w }, { data: t }] = await Promise.all([api.get("/wallet"), api.get("/wallet/transactions")]);
    setWallet(w); setTxs(t);
  };
  useEffect(() => { load(); }, []);

  const deposit = async () => {
    toast.info("Ödeme sistemi yakında aktif edilecektir. Bakiye yükleme şu an bakım modundadır.");
  };

  if (!wallet) return <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-emerald-500" /></div>;
  const bonusPct = config.launch_bonus_percent;

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <h1 className="text-3xl font-extrabold text-slate-900 mb-6">Cüzdanım & Bakiyem</h1>

      {/* Balance cards */}
      <div className="grid md:grid-cols-3 gap-4 mb-6">
        <div className="brand-gradient text-white rounded-2xl p-6 md:col-span-1" data-testid="wallet-available-balance">
          <div className="flex items-center gap-2 text-emerald-50 text-sm"><WalletIcon className="h-4 w-4" /> Kullanılabilir Bakiye</div>
          <div className="text-4xl font-extrabold mt-2">{tl(wallet.available_balance)}</div>
        </div>
        <div className="bg-white rounded-2xl border border-slate-200/80 p-6">
          <div className="text-sm text-slate-500">Gerçek Bakiye</div>
          <div className="text-2xl font-bold text-slate-900 mt-2" data-testid="wallet-real-balance">{tl(wallet.real_balance)}</div>
          <div className="text-xs text-slate-400 mt-1">Yüklediğiniz tutar</div>
        </div>
        <div className="bg-white rounded-2xl border border-violet-200 p-6">
          <div className="text-sm text-violet-600 flex items-center gap-1"><Gift className="h-4 w-4" /> Bonus Bakiye</div>
          <div className="text-2xl font-bold text-violet-700 mt-2" data-testid="wallet-bonus-balance">{tl(wallet.bonus_balance)}</div>
          <div className="text-xs text-slate-400 mt-1">Nakde çevrilemez, önce kullanılır</div>
        </div>
      </div>

      {/* Campaign banner */}
      <div className="rounded-2xl bg-gradient-to-r from-violet-500 to-emerald-500 text-white p-5 mb-6 flex items-center gap-3">
        <Sparkles className="h-6 w-6 shrink-0" />
        <div>
          <div className="font-extrabold">🎉 AÇILIŞA ÖZEL %{bonusPct} BONUS!</div>
          <div className="text-sm text-white/90">Cüzdanına yüklediğin tutarın %{bonusPct}'si Ustayla'dan hediye!</div>
        </div>
      </div>

      {/* Packages */}
      <div className="bg-white rounded-2xl border border-slate-200/80 p-6 mb-6">
        <h2 className="text-lg font-bold text-slate-900 mb-1">Bakiye Yükle</h2>
        <p className="text-sm text-slate-500 mb-3 flex items-center gap-1.5"><Lock className="h-3.5 w-3.5 text-emerald-600" /> iyzico güvenli altyapısı ve 256-bit SSL şifreleme ile korunan ödeme.</p>

        {/* Maintenance mode notice */}
        <div className="mb-4 rounded-xl border-2 border-amber-200 bg-amber-50 p-3 flex items-start gap-2.5" data-testid="wallet-maintenance-banner">
          <WrenchIcon className="h-5 w-5 text-amber-600 mt-0.5 shrink-0" />
          <div className="text-sm text-amber-800">
            <span className="font-bold">Ödeme sistemi yakında aktif edilecektir.</span> Bakiye yükleme şu an bakım modundadır; gerçek ödeme geçidi entegrasyonu tamamlanınca aktifleşecektir.
          </div>
        </div>

        {/* Mandatory agreement checkbox — right above the deposit buttons */}
        <label className="flex items-start gap-3 rounded-xl border-2 border-slate-200 has-[:checked]:border-emerald-400 has-[:checked]:bg-emerald-50/60 p-3 mb-4 cursor-pointer transition-colors" data-testid="wallet-agreement-label">
          <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} className="mt-0.5 h-5 w-5 shrink-0 accent-emerald-600" data-testid="wallet-agreement-checkbox" />
          <span className="text-sm text-slate-600 leading-snug">
            <Link to="/mesafeli-satis-sozlesmesi" target="_blank" className="text-emerald-700 font-semibold underline underline-offset-2" data-testid="link-mesafeli-satis">Mesafeli Satış Sözleşmesi</Link>'ni ve <Link to="/teslimat-ve-iade" target="_blank" className="text-emerald-700 font-semibold underline underline-offset-2" data-testid="link-teslimat-iade">Teslimat/İade Şartları</Link>'nı okudum, onaylıyorum.
          </span>
        </label>

        <div className={`grid grid-cols-2 sm:grid-cols-5 gap-3 transition-opacity ${agreed ? "" : "opacity-50"}`}>
          {config.wallet_packages.map((amt) => {
            const bonus = Math.round(amt * bonusPct / 100);
            const popular = amt === 150;
            return (
              <button key={amt} onClick={() => deposit(amt)} disabled={loading === amt || !agreed} data-testid={`wallet-package-${amt}-button`}
                className={`relative rounded-2xl border-2 p-4 text-center transition-colors disabled:cursor-not-allowed ${popular ? "border-emerald-500 bg-emerald-50" : "border-slate-200 hover:border-emerald-300"}`}>
                {popular && <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 text-[10px] font-bold bg-emerald-500 text-white px-2 py-0.5 rounded-full">En Popüler</span>}
                <div className="text-xl font-extrabold text-slate-900">{amt} TL</div>
                <div className="text-xs font-semibold text-violet-600 mt-1">+{bonus} TL bonus</div>
                <div className="text-[11px] text-slate-400 mt-1">= {amt + bonus} TL</div>
                {loading === amt && <Loader2 className="h-4 w-4 animate-spin mx-auto mt-2 text-emerald-500" />}
              </button>
            );
          })}
        </div>

        {/* Legal links below the deposit buttons */}
        <div className="mt-4 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-400" data-testid="wallet-legal-links">
          <Link to="/mesafeli-satis-sozlesmesi" className="hover:text-emerald-600">Mesafeli Satış Sözleşmesi</Link>
          <Link to="/teslimat-ve-iade" className="hover:text-emerald-600">Teslimat ve İade Şartları</Link>
          <Link to="/gizlilik-sozlesmesi" className="hover:text-emerald-600">Gizlilik Sözleşmesi (KVKK)</Link>
          <Link to="/hakkimizda" className="hover:text-emerald-600">Hakkımızda</Link>
        </div>

        <div className="mt-4 text-sm text-slate-600 bg-slate-50 rounded-xl p-3">
          <span className="font-semibold">Bilgi:</span> Ustayla'da 1 iletişim bilgisi açma = {tl(config.contact_unlock_price)}. Harcamanız önce bonus bakiyenizden düşer.
        </div>
      </div>

      {/* Secure Payment strip — right below the deposit form */}
      <div className="bg-white rounded-2xl border border-slate-200/80 p-5 mb-6" data-testid="secure-payment-strip">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2.5">
            <span className="h-10 w-10 rounded-full bg-emerald-50 flex items-center justify-center text-emerald-600"><ShieldCheck className="h-5 w-5" /></span>
            <div>
              <div className="text-sm font-bold text-slate-900 flex items-center gap-1.5"><Lock className="h-3.5 w-3.5 text-emerald-600" /> 256-Bit SSL Sertifikası ile Güvenli Ödeme</div>
              <div className="text-xs text-slate-400">Kart bilgileriniz Ustayla sunucularında saklanmaz.</div>
            </div>
          </div>
          <div className="flex items-center gap-2.5">
            {/* Visa */}
            <span className="inline-flex items-center h-9 px-3 rounded-md bg-white border border-slate-200 shadow-sm" title="Visa">
              <span className="italic font-extrabold text-[#1A1F71] text-lg tracking-tight">VISA</span>
            </span>
            {/* Mastercard */}
            <span className="inline-flex items-center h-9 px-3 rounded-md bg-white border border-slate-200 shadow-sm" title="Mastercard">
              <span className="flex items-center">
                <span className="h-5 w-5 rounded-full bg-[#EB001B]"></span>
                <span className="h-5 w-5 rounded-full bg-[#F79E1B] -ml-2 mix-blend-multiply"></span>
              </span>
              <span className="ml-1.5 text-[11px] font-semibold text-slate-700 lowercase">mastercard</span>
            </span>
            {/* iyzico */}
            <span className="inline-flex items-center h-9 px-3 rounded-md bg-white border border-slate-200 shadow-sm" title="iyzico ile Öde">
              <span className="font-extrabold text-[#1E64FF] text-base lowercase">iyzico</span>
              <span className="ml-1 text-[11px] text-slate-400">ile öde</span>
            </span>
          </div>
        </div>
      </div>

      {/* Transactions */}
      <div className="bg-white rounded-2xl border border-slate-200/80 p-6">
        <h2 className="text-lg font-bold text-slate-900 mb-4">İşlem Geçmişi</h2>
        {txs.length === 0 ? <p className="text-sm text-slate-500">Henüz işlem yok.</p> : (
          <div className="space-y-2">
            {txs.map((t) => {
              const meta = TX_LABELS[t.type] || { label: t.type, icon: Sparkles, color: "text-slate-600" };
              const Icon = meta.icon;
              const positive = t.amount > 0;
              return (
                <div key={t.id} className="flex items-center justify-between py-3 border-b border-slate-100 last:border-0" data-testid={`tx-${t.id}`}>
                  <div className="flex items-center gap-3">
                    <span className={`h-9 w-9 rounded-full bg-slate-50 flex items-center justify-center ${meta.color}`}><Icon className="h-4 w-4" /></span>
                    <div>
                      <div className="font-semibold text-slate-900 text-sm">{meta.label}</div>
                      <div className="text-xs text-slate-400">{t.description} • {new Date(t.created_at).toLocaleString("tr-TR")}</div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className={`font-bold ${positive ? "text-emerald-600" : "text-slate-700"}`}>{positive ? "+" : ""}{tl(t.amount)}</div>
                    <div className="text-[11px] text-slate-400 flex items-center gap-1 justify-end"><Check className="h-3 w-3" /> {t.status === "completed" ? "Tamamlandı" : t.status}</div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
